import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  TransferState,
} from '@angular/core';
import { environment } from './environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { CookieService } from 'ngx-cookie-service';
import {
  HttpClient,
  HttpErrorResponse,
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app/app-routing';
import { JwtInterceptor } from './app/interceptor/jwt.interceptor';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { ErrorInterceptor } from './app/interceptor/error.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import localeFr from '@angular/common/locales/fr-CA';
import localeEn from '@angular/common/locales/en-CA';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration } from '@sentry/angular';
import { AuthenticationService } from './app/services/authentication.service';
import {
  ChunkLoadErrorHandler,
  clearChunkReloadFlag,
} from './app/utils/chunkLoadError';

registerLocaleData(localeFr, 'fr-CA');
registerLocaleData(localeEn, 'en-CA');

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      RouterModule.forRoot(routes, {
        scrollPositionRestoration: 'enabled',
        onSameUrlNavigation: 'reload',
        enableTracing: environment.tracing,
        anchorScrolling: 'enabled',
      }),
    ),
    { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
    CookieService,
    provideHttpClient(
      withXhr(),
      withInterceptors([JwtInterceptor, ErrorInterceptor]),
    ),
    {
      provide: ErrorHandler,
      useValue: new ChunkLoadErrorHandler(Sentry.createErrorHandler()),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideOAuthClient(),
    { provide: LOCALE_ID, useValue: 'fr-CA' },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient, TransferState],
        },
      }),
    ),
    provideAnimations(),
  ],
})
  .then((appRef) => {
    const injector = appRef.injector;
    const authenticationService = injector.get(AuthenticationService);

    // App booted successfully — allow a future deploy to trigger a reload again.
    clearChunkReloadFlag();

    if (environment.sendToSentry) {
      Sentry.init({
        dsn: 'https://dd1f0b9b23f1741dfc77867a0a5fd253@o4509083997962240.ingest.us.sentry.io/4509084008513536',
        maxValueLength: 100000,
        beforeSend(event, hint) {
          try {
            const err: any = hint?.originalException;

            // 1) Filtrer explicitement le bruit OIDC seulement
            const OIDC_TYPES = new Set([
              'invalid_nonce_in_state',
              'login_required',
              'token_refresh_error',
              'token_error', // v20+ emits this for code-flow refresh failures (was 'token_refresh_error' in v17)
              'silent_refresh_timeout',
              'interaction_required',
              'consent_required',
              'access_denied',
              'session_changed',
              'discovery_document_load_error',
              'jwks_load_error',
              'token_validation_error',
              'user_cancelled',
              'popup_closed_by_user',
            ]);

            const msg = String(
              event?.message ||
                err?.message ||
                err?.reason ||
                event?.exception?.values?.[0]?.value ||
                '',
            );

            const isOidcType =
              (err &&
                typeof err === 'object' &&
                (OIDC_TYPES.has(err.type) || OIDC_TYPES.has(err?.error))) ||
              [...OIDC_TYPES].some((t) => msg.includes(t)) ||
              /(^|[\s_])(openid|oauth|oidc|jwks|well-known\/openid-configuration)([\s_/]|$)/i.test(
                msg,
              ) ||
              // Certains HttpErrorResponse pointent directement vers les endpoints OIDC
              (err?.url &&
                /oauth|openid|authorize|token|\.well-known\/openid-configuration|jwks/i.test(
                  String(err.url),
                ));

            //if (isOidcType) return null; // ❌ on ignore uniquement OIDC

            // 2) (Optionnel) garder ton filtre réseau existant si tu veux continuer
            if (
              err instanceof HttpErrorResponse &&
              [0, 401, 503, 504].includes(err.status)
            ) {
              return null;
            }
            if (
              err &&
              typeof err === 'object' &&
              'status' in err &&
              [0, 401, 503, 504].includes(err.status)
            ) {
              return null;
            }
            const httpMsg =
              typeof event?.message === 'string' ? event.message : '';
            if (
              /Http failure response .*:\s*(0|401|503|504)\b/i.test(httpMsg)
            ) {
              return null;
            }
            const exVal = event?.exception?.values?.[0];
            const exMsg = typeof exVal?.value === 'string' ? exVal.value : '';
            if (/Http failure response .*:\s*(0|401|503|504)\b/i.test(exMsg)) {
              return null;
            }
            const httpStatusTag = (event as any)?.tags?.['http.status_code'];
            if ([0, 401, 503, 504].includes(Number(httpStatusTag))) {
              return null;
            }

            // 3) Set User
            event.user = {
              ...event.user,
              id: authenticationService.getIdUser(),
              username: authenticationService.getUserName(),
              ip_address: '{{auto}}',
            };

            // 4) Set extras
            event.extra = {
              ...event.extra,
              timestamp: new Date().toISOString(),
              employee_id: authenticationService.getIdEmployee(),
              company_id: authenticationService.getIdCompany(),
            };

            return event;
          } catch {
            return event;
          }
        },
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/clientmobile\.rpa360\.com/,
          /^https:\/\/mobile\.rpa\.accpt\.udata\.ca/,
          /^https:\/\/clientmobile\.accpt\.rpa360\.com/,
        ],
        ignoreErrors: [
          'Token has expired',
          /invalid_grant/,
          /invalid_nonce_in_state/i,
          /login_required/i,
          /token[_ ]?refresh[_ ]?error/i,
          /silent[_ ]?refresh[_ ]?timeout/i,
          /openid|jwks|well-known\/openid-configuration/i,
        ],
        integrations: [browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate:
          environment.tracesSampleRate > 0 ? environment.tracesSampleRate : 0,
        replaysSessionSampleRate:
          environment.replaysSessionSampleRate > 0
            ? environment.replaysSessionSampleRate
            : 0,
        replaysOnErrorSampleRate:
          environment.replaysOnErrorSampleRate > 0
            ? environment.replaysOnErrorSampleRate
            : 0,
      });
    }
  })
  .catch((err) => console.log(err));

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}
